"use client";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Select from "react-select";

// Phase 3 (B1): entry point for the Répartition report. The user picks a building
// and either one period (single-period mode) or two periods (comparison mode).
// "Generate PDF" navigates to the renderer (built in C1a) with the selection in
// the URL so the export can be shared and re-run deterministically.
//
// This page mirrors the building+period selector pattern already used by
// src/pages/Reports/Index.js so it feels familiar to operators.

const authHeaders = () => ({
  "api-key": process.env.REACT_APP_API_KEY,
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const RepartitionReportIndex = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-driven initial state — keeps a refreshed page or a shared link in sync.
  const buildingParamInit = searchParams.get("building");
  const mainPeriodParamInit = searchParams.get("main_period");
  const comparePeriodParamInit = searchParams.get("compare_period");

  const [availableBuildings, setAvailableBuildings] = useState([]);
  const [availablePeriods, setAvailablePeriods] = useState([]);

  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedMainPeriod, setSelectedMainPeriod] = useState(null);
  const [selectedComparePeriod, setSelectedComparePeriod] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ─── helpers ───────────────────────────────────────────────────────────────

  const formatPeriodLabel = (p) =>
    `${new Date(p.start_date).toLocaleDateString()} – ${new Date(p.end_date).toLocaleDateString()}`;

  const fetchBuildings = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}building/list`,
        { headers: authHeaders() }
      );
      if (res?.data?.status === "success") {
        const list = res.data.data.buildings || [];
        return list.map((b) => ({ value: b.id, label: b.building_name }));
      }
      throw new Error(res?.data?.message || "Failed to load buildings");
    } catch (e) {
      console.error("Error fetching buildings:", e);
      // Re-throw so the caller can surface a user-facing error.
      throw e;
    }
  };

  const fetchPeriods = async (buildingId) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}building/${buildingId}/periods`,
        { headers: authHeaders() }
      );
      if (res?.data?.status === "success") {
        const list = res.data.data.periods || [];
        return list.map((p) => ({ value: p.id, label: formatPeriodLabel(p) }));
      }
      throw new Error(res?.data?.message || "Failed to load periods");
    } catch (e) {
      console.error("Error fetching periods:", e);
      throw e;
    }
  };

  // Extracted to keep the initial-load useEffect simple: restore building +
  // periods from the URL params (if any). Returns silently when the URL had
  // no params or the IDs don't match anything.
  const restoreFromUrl = async (buildings) => {
    if (!buildingParamInit) return;
    const match = buildings.find((b) => String(b.value) === String(buildingParamInit));
    if (!match) return;
    setSelectedBuilding(match);
    const periods = await fetchPeriods(match.value);
    setAvailablePeriods(periods);
    if (mainPeriodParamInit) {
      const mp = periods.find((p) => String(p.value) === String(mainPeriodParamInit));
      if (mp) setSelectedMainPeriod(mp);
    }
    if (comparePeriodParamInit) {
      const cp = periods.find((p) => String(p.value) === String(comparePeriodParamInit));
      if (cp) setSelectedComparePeriod(cp);
    }
  };

  // ─── initial load + URL restoration ────────────────────────────────────────

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const buildings = await fetchBuildings();
        setAvailableBuildings(buildings);
        await restoreFromUrl(buildings);
      } catch (e) {
        setError(e?.message || "Could not load buildings. Please try again.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync state back to URL so a refresh / shared link keeps the selection.
  useEffect(() => {
    const next = new URLSearchParams();
    if (selectedBuilding) next.set("building", String(selectedBuilding.value));
    if (selectedMainPeriod) next.set("main_period", String(selectedMainPeriod.value));
    if (selectedComparePeriod) next.set("compare_period", String(selectedComparePeriod.value));
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [selectedBuilding, selectedMainPeriod, selectedComparePeriod, searchParams, setSearchParams]);

  // ─── interactions ──────────────────────────────────────────────────────────

  const handleBuildingChange = async (opt) => {
    setSelectedBuilding(opt);
    setSelectedMainPeriod(null);
    setSelectedComparePeriod(null);
    setError("");
    if (!opt) {
      setAvailablePeriods([]);
      return;
    }
    setLoading(true);
    try {
      const periods = await fetchPeriods(opt.value);
      setAvailablePeriods(periods);
    } catch (e) {
      setError(e?.message || "Could not load periods for this building.");
      setAvailablePeriods([]);
    } finally {
      setLoading(false);
    }
  };

  // The main and compare period selects must not let the user pick the same
  // period twice — that would silently turn comparison off without warning.
  const comparePeriodOptions = useMemo(() => {
    if (!selectedMainPeriod) return availablePeriods;
    return availablePeriods.filter((p) => p.value !== selectedMainPeriod.value);
  }, [availablePeriods, selectedMainPeriod]);

  const handleGenerate = () => {
    if (!selectedBuilding || !selectedMainPeriod) {
      setError("Please select a building and a main period.");
      return;
    }
    setError("");
    const params = new URLSearchParams();
    params.set("building", String(selectedBuilding.value));
    params.set("main_period", String(selectedMainPeriod.value));
    if (selectedComparePeriod) {
      params.set("compare_period", String(selectedComparePeriod.value));
    }
    // Final renderer route comes online in C1a. For now we navigate to a hash
    // that won't 404 if visited directly; the renderer will replace it.
    navigate(`/repartition-report/preview?${params.toString()}`);
  };

  // ─── render ────────────────────────────────────────────────────────────────

  return (
    <div className="admin-page">
      <div className="admin-page-title d-flex align-items-center justify-content-between">
        <h1 className="page-section-title mb-0">Répartition Report</h1>
      </div>

      <div className="key-card mb-4 p-lg-4 p-3 mt-4">
        <h5 className="mb-3">Generate Annual Distribution Report</h5>
        <p className="text-muted small mb-4">
          Pick a building and a main period. Optionally add a comparison period
          to include year-over-year differences in every section. White-label
          branding and layout follow the per-period settings.
        </p>

        <div className="row g-3">
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="rr-building">Building</label>
              <div className="custom-select">
                <Select
                  inputId="rr-building"
                  options={availableBuildings}
                  value={selectedBuilding}
                  onChange={handleBuildingChange}
                  placeholder="Select building…"
                  isClearable
                  isDisabled={loading}
                />
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="rr-main-period">Main period</label>
              <div className="custom-select">
                <Select
                  inputId="rr-main-period"
                  options={availablePeriods}
                  value={selectedMainPeriod}
                  onChange={setSelectedMainPeriod}
                  placeholder="Select main period…"
                  isClearable
                  isDisabled={!selectedBuilding || loading}
                />
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="rr-compare-period">
                Comparison period <span className="text-muted">(optional)</span>
              </label>
              <div className="custom-select">
                <Select
                  inputId="rr-compare-period"
                  options={comparePeriodOptions}
                  value={selectedComparePeriod}
                  onChange={setSelectedComparePeriod}
                  placeholder="Select compare period…"
                  isClearable
                  isDisabled={!selectedMainPeriod || loading}
                />
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-warning mt-3 mb-0 py-2 small">
            {error}
          </div>
        )}

        <div className="mt-4 d-flex gap-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={!selectedBuilding || !selectedMainPeriod || loading}
          >
            Generate PDF
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => {
              setSelectedBuilding(null);
              setSelectedMainPeriod(null);
              setSelectedComparePeriod(null);
              setAvailablePeriods([]);
              setError("");
            }}
            disabled={loading}
          >
            Reset
          </button>
        </div>

        {selectedBuilding && selectedMainPeriod && (
          <div className="mt-4 small text-muted">
            Mode:{" "}
            <strong>
              {selectedComparePeriod ? "Two-period comparison" : "Single period"}
            </strong>
            {" — "}
            {selectedComparePeriod
              ? "Diff and Diff % columns will be shown next to the main period."
              : "Diff columns will be hidden."}
          </div>
        )}
      </div>
    </div>
  );
};

export default RepartitionReportIndex;
